import type { FC } from 'react';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  IconButton,
  Typography,
} from '@mui/material';
import { createUseStyles } from 'react-jss';

import type { LcpnTheme } from '../../styles/theme';
import { Icon } from '../Icon';
import { useFavouritesActions } from '../../providers/FavouritesProvider';
import { useMapData } from '../../map-data/use-map-data';
import { usePoiSelectedLocations } from '../PointOfInterestExperience/PoiSelectedLocationsProvider';
import { useHeatMapSelectedLocations } from '../HeatMapExperience/HeatMapSelectedLocationsProvider';
import {
  useUpdateNavigationBar,
  useNavigationBarData,
} from '../NavigationBar/NavigationBarProvider';

const MAX_COMPARISON_ITEMS = 4;

const useStyles = createUseStyles((theme: LcpnTheme) => ({
  listItem: {
    borderBottom: `1px solid ${theme.colors['grey-100']}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  listItemButton: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  removeButton: {
    color: theme.colors['grey-80'],
    '&:hover': {
      color: '#e63946',
    },
  },
  checkbox: {
    padding: theme.spacing(1),
  },
}));

interface Props {
  type: 'poi' | 'heatmap';
  favourites: string[];
  selectedItems: string[];
  onToggleSelection: (id: string) => void;
}

export const FavouritesList: FC<Props> = ({
  type,
  favourites,
  selectedItems,
  onToggleSelection,
}) => {
  const classes = useStyles();
  const { togglePoiFavourite, toggleHeatmapFavourite } = useFavouritesActions();
  const { studiesById, countryHeatMapMetaData } = useMapData();

  const {
    setSelectedProgramId,
    setShowProgramCard,
    setShowCountryCard,
    setShowCityCard,
    setSelectedCountryName,
    setSelectedCountrySubDivisionName,
  } = usePoiSelectedLocations();

  const {
    setSelectedCountryCode: setSelectedHeatmapCountryCode,
    setShowCountryCard: setShowHeatmapCountryCard,
  } = useHeatMapSelectedLocations();

  const { setSelectedExperience } = useNavigationBarData();
  const { resetNavigationBar } = useUpdateNavigationBar();

  const handleNavigateToPoi = (studyId: string) => {
    const study = studiesById?.[studyId];
    if (!study) return;

    setSelectedCountryName(study.country);
    setSelectedCountrySubDivisionName({
      city: study.city,
      region: study.region,
    });
    setSelectedProgramId(studyId);
    setShowProgramCard(true);
    setShowCountryCard(false);
    setShowCityCard(false);
    setSelectedExperience('pinMap');
    resetNavigationBar();
  };

  const handleNavigateToHeatmap = (countryCode: string) => {
    setSelectedHeatmapCountryCode(countryCode);
    setShowHeatmapCountryCard(true);
    setSelectedExperience('heatMap');
    resetNavigationBar();
  };

  const handleRemove = (id: string) => {
    if (type === 'poi') {
      togglePoiFavourite(id);
    } else {
      toggleHeatmapFavourite(id);
    }
  };

  const getName = (id: string): string =>
    type === 'poi'
      ? studiesById?.[id]?.studyName || id
      : countryHeatMapMetaData?.[id]?.countryName || id;

  const getSecondaryInfo = (id: string): string => {
    if (type === 'poi') {
      const study = studiesById?.[id];
      return study ? `${study.country} - ${study.status}` : '';
    } else {
      const country = countryHeatMapMetaData?.[id];
      return country?.countryStatus || '';
    }
  };

  return (
    <List>
      {favourites.map(id => {
        const isSelected = selectedItems.includes(id);
        const canSelect =
          isSelected || selectedItems.length < MAX_COMPARISON_ITEMS;

        return (
          <ListItem
            key={id}
            className={classes.listItem}
            disablePadding
            secondaryAction={
              <IconButton
                edge="end"
                aria-label="remove from favourites"
                onClick={() => handleRemove(id)}
                className={classes.removeButton}
                size="small"
              >
                <Icon variant="Close" width={16} height={16} />
              </IconButton>
            }
          >
            <Checkbox
              edge="start"
              checked={isSelected}
              onChange={() => canSelect && onToggleSelection(id)}
              disabled={!canSelect}
              className={classes.checkbox}
            />
            <ListItemButton
              onClick={() =>
                type === 'poi'
                  ? handleNavigateToPoi(id)
                  : handleNavigateToHeatmap(id)
              }
              className={classes.listItemButton}
            >
              <ListItemText
                primary={
                  <Typography variant="body2" noWrap>
                    {getName(id)}
                  </Typography>
                }
                secondary={
                  <Typography variant="caption" noWrap>
                    {getSecondaryInfo(id)}
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );
};
